/**
 * LoginPage - User Authentication Page
 *
 * Migrated to use BasePage architecture.
 * Demonstrates: centerContent, custom headerProps, no footer, animated blob background.
 */
import { useMemo } from "react";
import { useLocation, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

// Hooks & Contexts
import { observer } from "mobx-react-lite";
import { useStore } from "@/stores";
import { useTranslation } from "@/shared/hooks";

// New Design System
import { BasePage } from "@/shared/ui/layout/BasePage";
import { Box } from "@/shared/ui/base";

// Components
import { LanguageSwitcher } from "@/shared/ui/components";
import { AuthCard } from "@/features/auth/components";

// Constants
import { AppRoutes } from "@/shared/constants";

interface LoginLocationState {
  error?: string;
}

export const LoginPage = observer(function LoginPage() {
  const location = useLocation();
  const { authStore } = useStore();
  const { status } = authStore;
  const t = useTranslation();

  const stateError = (location.state as LoginLocationState | null)?.error;

  const initialMode = useMemo(() => {
    if (stateError === "NEEDS_REGISTRATION" || status === "needs_registration")
      return "register";
    return "login";
  }, [status, stateError]);

  const initialErrorKey = useMemo(() => {
    if (stateError === "NEEDS_REGISTRATION" || status === "needs_registration")
      return "registrationRequired";
    if (stateError === "NOT_VERIFIED") return "notVerified";
    if (stateError === "FORBIDDEN") return "forbidden";
    return undefined;
  }, [status, stateError]);

  // Back button for header (using IconButton pattern via Link)
  const backButton = (
    <Link
      to={AppRoutes.home}
      className="flex items-center text-gray-500 hover:text-gray-900 transition-colors p-2 rounded-lg hover:bg-gray-100"
      aria-label={t("back") || "Back"}
    >
      <ArrowLeft className="w-5 h-5" />
    </Link>
  );

  return (
    <BasePage
      title={initialMode === "register" ? t("register") : t("signIn")}
      description="Sign in to your Apaato account or create a new one"
      showFooter={false}
      headerVariant="scroll-aware"
      maxWidth="sm"
      centerContent
      headerProps={{
        leftContent: backButton,
        showLogo: false,
        showAuthButton: false,
        rightContent: <LanguageSwitcher />,
      }}
    >
      {/* Animated blob background - single blob for login page */}
      <Box className="fixed top-0 left-0 w-full h-full flex items-center justify-center pointer-events-none -z-10">
        <Box className="w-[30rem] h-[30rem] bg-red-500 rounded-full mix-blend-multiply filter blur-2xl opacity-50 animate-blob" />
      </Box>

      {/* Auth Card - centered by BasePage */}
      <AuthCard initialMode={initialMode} initialErrorKey={initialErrorKey} />
    </BasePage>
  );
});
